import { useEffect, useState, useCallback } from "react";
import { IoIosLink } from "react-icons/io";
import { showErrorToast } from "../../../components/Constant/Notification";
import LocationService from "../../../components/Services/LocationService";
import CustomSelect from "../../../components/common/CustomSelect";
import Accordion from "../../../components/SetupComponents/PropertiesComponents/Accordion/Accordion";
import MediaUpload from "../../../components/SetupComponents/PropertiesComponents/MediaUpload";
import UserService from "../../../GlobalSetupComponents/Services/UserService";
import { roleIdProcurement } from "../../../GlobalSetupComponents/Enum/RoleEnum";
import MapWithPropertyAndData from "../../RoughDraft/RoughPropertyInfoManagement/MapWithPropertyAndData";
import CustomPropertySelect from "../../../components/common/CustomPropertySelect";
import RoughPropertyService from "../../Service/RoughPropertyService";
import PhoneInputField from "../../../components/common/PhoneInputField";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomDatePicker from "../../../Custom/Form/CustomDatePicker";
import ReusableDateInput from "../../../Custom/Form/CustomDatePicker";
import CustomDateInput from "../../../Custom/Form/CustomDatePicker";

const ProspectPropertyInfoBasic = ({
  propertyInfo,
  setPropertyInfo,
  handlePropertyInfoChange,
  handleArrayFieldChange,
  isEdit,
  propertyType = "layout", // Options: "layout", "acresAndCents", "resalePlot"
}) => {
  const [locationData, setLocationData] = useState([]);
  const [propertyNameOptions, setPropertyNameOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [inputValue, setInputValue] = useState(propertyInfo?.name || "");
  const [sourceBy, setSourceBy] = useState([]);

  const SQFT_TO_CENT = 435.6;
  const SQFT_TO_ACRE = 43560;
  const SQFT_TO_GROUND = 2400;

  const getDerivedAreas = (value, inputField) => {
    const numValue = parseFloat(value) || 0;
    let sqftValue = 0;

    // Convert input value to square feet based on the input field
    switch (inputField) {
      case "overAllAreaSqft":
        sqftValue = numValue;
        break;
      case "overAllAreaCent":
        sqftValue = numValue * SQFT_TO_CENT;
        break;
      case "overAllAreaAcres":
        sqftValue = numValue * SQFT_TO_ACRE;
        break;
      case "overAllAreaGround":
        sqftValue = numValue * SQFT_TO_GROUND;
        break;
      default:
        sqftValue = numValue;
    }

    // Calculate derived values for all units with 2 decimal places
    return {
      overAllAreaSqft: parseFloat(sqftValue.toFixed(4)) || "",
      overAllAreaCent: parseFloat((sqftValue / SQFT_TO_CENT).toFixed(4)),
      overAllAreaAcres: parseFloat((sqftValue / SQFT_TO_ACRE).toFixed(4)),
      overAllAreaGround: parseFloat((sqftValue / SQFT_TO_GROUND).toFixed(4)),
    };
  };

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const response = await LocationService.fetchLocation();
        const rawData = response.data || {};
        const formattedLocations = Object.entries(rawData).map(
          ([name, id]) => ({
            value: id,
            label: name,
          })
        );
        setLocationData(formattedLocations);
      } catch (error) {
        showErrorToast("Failed to load locations");
      }
    };
    fetchLocationData();
  }, []);

  useEffect(() => {
    const fetchUserRoleBasedData = async () => {
      try {
        const body = { roleIds: roleIdProcurement };
        const response = await UserService.getUserRoleBasedNames(body);
        const data = response.data?.users || [];
        setSourceBy(data);
      } catch (error) {
        showErrorToast("Failed to fetch user role-based data");
      }
    };
    fetchUserRoleBasedData();
  }, []);

  useEffect(() => {
    const fetchNames = async () => {
      try {
        const body = {
          searchKey: inputValue,
          collection: "prospectProperties",
        };
        const data = await RoughPropertyService.searchProperties(body);
        const options =
          data?.data?.data?.map((item) => ({
            value: item.id,
            label: item.name,
            plotNo: item.plotNo,
            rate: item.rate,
            sourcedBy: item.propertySourcedBy,
            status: item.status,
          })) || [];
        setPropertyNameOptions(options);
        setFilteredOptions(options);
      } catch (error) {
        setPropertyNameOptions([]);
        setFilteredOptions([]);
      }
    };
    fetchNames();
  }, [inputValue]);

  const handlePropertyNameInputChange = useCallback(
    (inputValue, actionMeta) => {
      setInputValue(inputValue);
      if (actionMeta.action === "input-change") {
        handlePropertyInfoChange({
          target: { name: "name", value: inputValue },
        });
        const filtered = propertyNameOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        );
        const optionsToShow = [...filtered];
        if (
          inputValue.trim() &&
          !propertyNameOptions.some(
            (option) => option.label.toLowerCase() === inputValue.toLowerCase()
          )
        ) {
          optionsToShow.push({
            value: inputValue.trim(),
            label: `Create "${inputValue.trim()}"`,
            isCustom: true,
          });
        }
        setFilteredOptions(optionsToShow);
      }
    },
    [propertyNameOptions, handlePropertyInfoChange]
  );

  const handlePropertyNameChange = (selectedOption) => {
    if (selectedOption) {
      handlePropertyInfoChange({
        target: { name: "name", value: selectedOption.label },
      });
      if (selectedOption.isCustom) {
        setInputValue(selectedOption.value);
      }
      setInputValue(selectedOption.label);
    } else {
      handlePropertyInfoChange({
        target: { name: "name", value: "" },
      });
      setInputValue("");
    }
  };

  const handleAreaChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);

    if (value !== "" && (isNaN(numValue) || numValue < 0)) {
      showErrorToast("Area cannot be negative or invalid");
      return;
    }

    // Calculate derived areas based on the input field
    const derivedAreas = getDerivedAreas(value, name);

    // Update all area fields
    setPropertyInfo((prev) => ({
      ...prev,
      ...derivedAreas,
    }));
  };

  const handleRateChange = (e) => {
    const { name, value } = e.target;
    if (value !== "" && parseFloat(value) < 0) {
      showErrorToast("Rate cannot be negative");
      return;
    }
    handlePropertyInfoChange({
      target: { name, value: value || "" },
    });
  };

  const handleLoanCashValueChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);

    // Allow the value to be set even if invalid, validation errors will show in UI
    handlePropertyInfoChange({
      target: { name, value: value || "" },
    });
  };

  const getValidationError = (field) => {
    const fieldValue = parseFloat(propertyInfo?.[field]) || 0;
    const loanMin = parseFloat(propertyInfo?.loanMinValue) || 0;
    const loanMax = parseFloat(propertyInfo?.loanMaxValue) || 0;
    const cashMin = parseFloat(propertyInfo?.cashMinValue) || 0;
    const cashMax = parseFloat(propertyInfo?.cashMaxValue) || 0;

    // Check for negative values first
    if (propertyInfo?.[field] && fieldValue < 0) {
      return "Value cannot be negative";
    }

    // Check for invalid numbers
    if (
      propertyInfo?.[field] &&
      propertyInfo?.[field] !== "" &&
      isNaN(fieldValue)
    ) {
      return "Please enter a valid number";
    }

    switch (field) {
      case "loanMinValue":
        return propertyInfo?.loanMinValue &&
          propertyInfo?.loanMaxValue &&
          loanMin > loanMax
          ? "Min value cannot be greater than max value"
          : null;
      case "loanMaxValue":
        return propertyInfo?.loanMinValue &&
          propertyInfo?.loanMaxValue &&
          loanMax < loanMin
          ? "Max value cannot be less than min value"
          : null;
      case "cashMinValue":
        return propertyInfo?.cashMinValue &&
          propertyInfo?.cashMaxValue &&
          cashMin > cashMax
          ? "Min value cannot be greater than max value"
          : null;
      case "cashMaxValue":
        return propertyInfo?.cashMinValue &&
          propertyInfo?.cashMaxValue &&
          cashMax < cashMin
          ? "Max value cannot be less than min value"
          : null;
      default:
        return null;
    }
  };

  // Get geo location label based on property type
  const getGeoLocationLabel = () => {
    switch (propertyType) {
      case "layout":
        return "Type Latitude and longitude";
      case "acresAndCents":
        return "Type Latitude and longitude";
      case "resalePlot":
        return "Type Latitude and longitude";
      default:
        return "Type Latitude and longitude";
    }
  };

  // Get number of units label based on property type
  const getUnitsLabel = () => {
    switch (propertyType) {
      case "layout":
        return "Number of Units";
      case "acresAndCents":
        return "Number of Units";
      case "resalePlot":
        return "Number of Units";
      default:
        return "Number of Units";
    }
  };

  const accordionItems = [
    {
      title: "Basic Info",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 pb-24 gap-6 p-6">
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Property Name
            </label>
            <div className="w-[72%]">
              <CustomPropertySelect
                value={
                  propertyInfo?.name
                    ? { label: propertyInfo.name, value: propertyInfo.name }
                    : null
                }
                options={filteredOptions}
                isClearable
                isSearchable
                onChange={handlePropertyNameChange}
                onInputChange={handlePropertyNameInputChange}
                inputValue={inputValue}
                placeholder="Search or Type Property Name"
                className="flex-1"
                noOptionsMessage={({ inputValue }) =>
                  inputValue
                    ? `No results found. Keep typing to add "${inputValue}"`
                    : "Start typing to search properties"
                }
                filterOption={() => true}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              Property ID
            </label>
            <input
              type="text"
              name="propertyId"
              value={propertyInfo?.propertyId || ""}
              onChange={handlePropertyInfoChange}
              className="flex-1 px-4 py-2 w-full input-focus"
              placeholder="Enter Property ID"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              {getUnitsLabel()}
            </label>
            <input
              type="number"
              name="noOfUnits"
              min={1}
              value={propertyInfo?.noOfUnits || ""}
              onChange={handlePropertyInfoChange}
              onWheel={(e) => e.target.blur()}
              className="flex-1 px-4 py-2 w-full input-focus no-arrow"
              placeholder={`Enter ${getUnitsLabel()}`}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Date of Property Came
            </label>
            <CustomDateInput
              value={propertyInfo?.propertyEnteredDate || ""}
              onChange={handlePropertyInfoChange}
              disabled={propertyInfo?.propertyEnteredDate ? isEdit : false}
              name="propertyEnteredDate"
              placeholder="Select Date"
            />
            {/* <input
              type="date"
              disabled={propertyInfo?.propertyEnteredDate ? isEdit : false}
              name="propertyEnteredDate"
              value={propertyInfo?.propertyEnteredDate || ""}
              onChange={handlePropertyInfoChange}
              className="flex-1 px-4 py-2 input-focus"
              placeholder="Select Date"
              max={new Date().toISOString().split("T")[0]}
            /> */}
          </div>
        </div>
      ),
    },
    {
      title: "Property Area & Rate",
      content: (
        <div className="space-y-8 p-6 ">
          {/* Overall Area */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-start">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Overall Area
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: "overAllAreaSqft", label: "SQ.FT" },
                { name: "overAllAreaCent", label: "Cents" },
                { name: "overAllAreaAcres", label: "Acres" },
                { name: "overAllAreaGround", label: "Grounds" },
              ].map((field) => (
                <div key={field.name} className="relative">
                  <input
                    type="number"
                    name={field.name}
                    value={propertyInfo?.[field.name] || ""}
                    onChange={handleAreaChange}
                    onWheel={(e) => e.target.blur()}
                    placeholder={`Type ${field.label}`}
                    className="w-full px-4 py-2 input-focus no-arrow"
                    step="any"
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "+") {
                        e.preventDefault();
                      }
                      if (e.key === "e") {
                        e.preventDefault();
                      }
                    }}
                  />
                  <span className="absolute right-2 top-2 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                    {field.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Marketing Rate Per */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Marketing Rate Per
            </label>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="w-56">
                <CustomSelect
                  options={[
                    { value: "sqft", label: "SQ.FT" },
                    { value: "cent", label: "Cent" },
                    { value: "ground", label: "Ground" },
                    { value: "acres", label: "Acres" },
                  ]}
                  value={
                    propertyInfo?.marketingRatePer
                      ? {
                          value: propertyInfo.marketingRatePer,
                          label: propertyInfo.marketingRatePer.toUpperCase(),
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: {
                        name: "marketingRatePer",
                        value: selectedOption?.value || "",
                      },
                    })
                  }
                  placeholder="Select Unit"
                  isClearable={false}
                />
              </div>
              <input
                type="number"
                name="marketingRatePerValue"
                value={propertyInfo?.marketingRatePerValue || ""}
                onChange={handleRateChange}
                onWheel={(e) => e.target.blur()}
                className="flex-1 px-4 py-2 input-focus no-arrow"
                placeholder="Type Rate"
                min="0"
                step="any"
              />
            </div>
          </div>

          {/* Vendor Rate Per */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Vendor Rate Per
            </label>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="w-56">
                <CustomSelect
                  options={[
                    { value: "sqft", label: "SQ.FT" },
                    { value: "cent", label: "Cent" },
                    { value: "ground", label: "Ground" },
                    { value: "acres", label: "Acres" },
                  ]}
                  value={
                    propertyInfo?.vendorRatePer
                      ? {
                          value: propertyInfo.vendorRatePer,
                          label: propertyInfo.vendorRatePer.toUpperCase(),
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: {
                        name: "vendorRatePer",
                        value: selectedOption?.value || "",
                      },
                    })
                  }
                  placeholder="Select Unit"
                  isClearable={false}
                />
              </div>
              <input
                type="number"
                name="vendorRatePerValue"
                value={propertyInfo?.vendorRatePerValue || ""}
                onChange={handleRateChange}
                onWheel={(e) => e.target.blur()}
                className="flex-1 px-4 py-2 input-focus no-arrow"
                placeholder="Type Rate"
                min="0"
                step="any"
              />
            </div>
          </div>

          {/* Mediator or Trader Commission */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              Mediator or Trader Commission
            </label>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="w-56">
                <CustomSelect
                  options={[
                    { label: "Percentage", value: "Percentage" },
                    { label: "Amount", value: "Amount" },
                  ]}
                  value={
                    propertyInfo?.commissionType
                      ? {
                          value: propertyInfo.commissionType,
                          label: propertyInfo.commissionType,
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: {
                        name: "commissionType",
                        value: selectedOption?.value || "",
                      },
                    })
                  }
                  placeholder="Select Type"
                  isClearable={false}
                />
              </div>
              <input
                type="number"
                name="commissionValue"
                value={propertyInfo?.commissionValue || ""}
                onChange={handleRateChange}
                onWheel={(e) => e.target.blur()}
                className="flex-1 px-4 py-2 input-focus no-arrow"
                placeholder="Type Value"
                min="0"
                step="any"
              />
            </div>
          </div>

          {/* Loanable & Cashable Value Minimum */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-start">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              Loanable & Cashable Value Minimum
            </label>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <div className="w-56">
                  <CustomSelect
                    options={[
                      { label: "Calculate with sq.ft", value: "sq.ft" },
                      {
                        label: "Calculate with overall value",
                        value: "overall",
                      },
                    ]}
                    value={
                      propertyInfo?.loanOrCashMinCalculateBy
                        ? {
                            value: propertyInfo.loanOrCashMinCalculateBy,
                            label:
                              propertyInfo.loanOrCashMinCalculateBy === "sq.ft"
                                ? "Calculate with sq.ft"
                                : "Calculate with overall value",
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handlePropertyInfoChange({
                        target: {
                          name: "loanOrCashMinCalculateBy",
                          value: selectedOption?.value || "",
                        },
                      })
                    }
                    placeholder="Select Calculation"
                    isClearable={false}
                  />
                </div>

                <div className="flex-1 space-y-2">
                  <div className="relative">
                    <input
                      type="number"
                      name="loanMinValue"
                      value={propertyInfo?.loanMinValue || ""}
                      onChange={handleLoanCashValueChange}
                      onWheel={(e) => e.target.blur()}
                      className={`w-full px-4 py-2 input-focus no-arrow ${
                        getValidationError("loanMinValue")
                          ? "border-red-500"
                          : ""
                      }`}
                      placeholder="Type Min Value"
                      min="0"
                      step="any"
                    />
                    <span className="absolute right-2 top-2 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                      Loan
                    </span>
                  </div>
                  {getValidationError("loanMinValue") && (
                    <span className="text-red-500 text-xs">
                      {getValidationError("loanMinValue")}
                    </span>
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <div className="relative">
                    <input
                      type="number"
                      name="cashMinValue"
                      value={propertyInfo?.cashMinValue || ""}
                      onChange={handleLoanCashValueChange}
                      onWheel={(e) => e.target.blur()}
                      className={`w-full px-4 py-2 input-focus no-arrow ${
                        getValidationError("cashMinValue")
                          ? "border-red-500"
                          : ""
                      }`}
                      placeholder="Type Min Value"
                      min="0"
                      step="any"
                    />
                    <span className="absolute right-2 top-2 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                      Cash
                    </span>
                  </div>
                  {getValidationError("cashMinValue") && (
                    <span className="text-red-500 text-xs">
                      {getValidationError("cashMinValue")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Loanable & Cashable Value Maximum */}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-start">
            <label className="text-sm font-medium text-gray-700 flex items-center">
              Loanable & Cashable Value Maximum
            </label>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <div className="w-56">
                  <CustomSelect
                    options={[
                      { label: "Calculate with sq.ft", value: "sq.ft" },
                      {
                        label: "Calculate with overall value",
                        value: "overall",
                      },
                    ]}
                    value={
                      propertyInfo?.loanOrCashMaxCalculateBy
                        ? {
                            value: propertyInfo.loanOrCashMaxCalculateBy,
                            label:
                              propertyInfo.loanOrCashMaxCalculateBy === "sq.ft"
                                ? "Calculate with sq.ft"
                                : "Calculate with overall value",
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handlePropertyInfoChange({
                        target: {
                          name: "loanOrCashMaxCalculateBy",
                          value: selectedOption?.value || "",
                        },
                      })
                    }
                    placeholder="Select Calculation"
                    isClearable={false}
                  />
                </div>

                <div className="flex-1 space-y-2">
                  <div className="relative">
                    <input
                      type="number"
                      name="loanMaxValue"
                      value={propertyInfo?.loanMaxValue || ""}
                      onChange={handleLoanCashValueChange}
                      onWheel={(e) => e.target.blur()}
                      className={`w-full px-4 py-2 input-focus no-arrow ${
                        getValidationError("loanMaxValue")
                          ? "border-red-500"
                          : ""
                      }`}
                      placeholder="Type Max Value"
                      min="0"
                      step="any"
                    />
                    <span className="absolute right-2 top-2 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                      Loan
                    </span>
                  </div>
                  {getValidationError("loanMaxValue") && (
                    <span className="text-red-500 text-xs">
                      {getValidationError("loanMaxValue")}
                    </span>
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <div className="relative">
                    <input
                      type="number"
                      name="cashMaxValue"
                      value={propertyInfo?.cashMaxValue || ""}
                      onChange={handleLoanCashValueChange}
                      onWheel={(e) => e.target.blur()}
                      className={`w-full px-4 py-2 input-focus no-arrow ${
                        getValidationError("cashMaxValue")
                          ? "border-red-500"
                          : ""
                      }`}
                      placeholder="Type Max Value"
                      min="0"
                      step="any"
                    />
                    <span className="absolute right-2 top-2 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                      Cash
                    </span>
                  </div>
                  {getValidationError("cashMaxValue") && (
                    <span className="text-red-500 text-xs">
                      {getValidationError("cashMaxValue")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Document Info",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          {/* Doc Received or Not */}
          <div className="flex items-center gap-4">
            <label className="w-1/3 text-sm font-medium text-gray-700">
              Doc Received or Not
            </label>
            <div className="w-2/3">
              <CustomSelect
                options={[
                  { value: true, label: "Received" },
                  { value: false, label: "Not Received" },
                ]}
                value={
                  propertyInfo?.docReceivedOrNot !== undefined
                    ? {
                        value: propertyInfo.docReceivedOrNot,
                        label: propertyInfo.docReceivedOrNot
                          ? "Received"
                          : "Not Received",
                      }
                    : null
                }
                onChange={(selectedOption) =>
                  handlePropertyInfoChange({
                    target: {
                      name: "docReceivedOrNot",
                      value: selectedOption?.value ?? false,
                    },
                  })
                }
                placeholder="Select Option"
                isClearable
                className="w-full"
              />
            </div>
          </div>

          {/* Doc Received Date */}
          <div className="flex items-center gap-4">
            <label className="w-1/3 text-sm font-medium text-gray-700">
              Doc. Received Date
            </label>
            <input
              type="date"
              name="docReceivedDate"
              value={propertyInfo?.docReceivedDate || ""}
              onChange={handlePropertyInfoChange}
              disabled={propertyInfo?.docReceivedDate ? isEdit : false}
              className="w-2/3 px-4 py-2 input-focus"
              placeholder="Select Date"
            />
          </div>

          {/* Full Set of Doc Received Date */}
          <div className="flex items-center gap-4">
            <label className="w-1/3 text-sm font-medium text-gray-700">
              Full Set of Doc Received Date
            </label>
            <input
              type="date"
              name="fullSetOfDocReceivedDate"
              value={propertyInfo?.fullSetOfDocReceivedDate || ""}
              onChange={handlePropertyInfoChange}
              disabled={propertyInfo?.fullSetOfDocReceivedDate ? isEdit : false}
              className="w-2/3 px-4 py-2 input-focus"
              placeholder="Select Date"
            />
          </div>
        </div>
      ),
    },
    {
      title: "Property Sourced By Info",
      content: (
        <div className="grid grid-cols-1 pb-32 md:grid-cols-2 gap-6 p-6">
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
              <span className="w-1 h-6 bg-red-500 mr-2 rounded-sm"></span>
              Primary Source
            </label>
            <CustomSelect
              options={sourceBy}
              value={
                propertyInfo?.primarySourcedBy && sourceBy?.length > 0
                  ? sourceBy.find(
                      (s) => s.value === propertyInfo.primarySourcedBy
                    ) || null
                  : null
              }
              onChange={(selectedOption) =>
                handlePropertyInfoChange({
                  target: {
                    name: "primarySourcedBy",
                    value: selectedOption?.value || "",
                  },
                })
              }
              placeholder="Select Property Source"
              className="flex-1"
              isSearchable
              dropdownHeight="130px"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm font-medium text-gray-700">
              Secondary Source
            </label>
            <CustomSelect
              options={sourceBy}
              value={
                propertyInfo?.secondarySourcedBy && sourceBy?.length > 0
                  ? sourceBy.find(
                      (s) => s.value === propertyInfo.secondarySourcedBy
                    ) || null
                  : null
              }
              onChange={(selectedOption) =>
                handlePropertyInfoChange({
                  target: {
                    name: "secondarySourcedBy",
                    value: selectedOption?.value || "",
                  },
                })
              }
              placeholder="Select Property Source"
              className="flex-1"
              isSearchable
              dropdownHeight="130px"
            />
          </div>
        </div>
      ),
    },
    {
      title: "Vendor, Mediator & Trader Info",
      content: (
        <div className="space-y-6 p-6 pb-32">
          {[
            { label: "Vendor", name: "vendor" },
            { label: "Mediator", name: "mediator" },
            { label: "Trader", name: "trader" },
          ].map((person) => (
            <div
              key={person.name}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm font-medium text-gray-700">
                  {person.label} Name
                </label>
                <input
                  type="text"
                  name={`${person.name}Name`}
                  value={propertyInfo?.[`${person.name}Name`] || ""}
                  onChange={handlePropertyInfoChange}
                  className="flex-1 px-4 py-2 input-focus"
                  placeholder={`Enter ${person.label} Name`}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-40 text-sm font-medium text-gray-700">
                  {person.label} Number
                </label>
                <div className="flex-1">
                  <PhoneInputField
                    value={
                      propertyInfo?.[`${person.name}CountryCode`] &&
                      propertyInfo?.[`${person.name}PhoneNumber`]
                        ? `${propertyInfo[`${person.name}CountryCode`]}${
                            propertyInfo[`${person.name}PhoneNumber`]
                          }`
                        : propertyInfo?.[`${person.name}PhoneNumber`] || ""
                    }
                    onChange={(value) => {
                      if (value && value.length > 10) {
                        const countryCode = value.slice(0, -10);
                        const phoneNumber = value.slice(-10);
                        handlePropertyInfoChange({
                          target: {
                            name: `${person.name}CountryCode`,
                            value: countryCode,
                          },
                        });
                        handlePropertyInfoChange({
                          target: {
                            name: `${person.name}PhoneNumber`,
                            value: phoneNumber,
                          },
                        });
                      } else {
                        handlePropertyInfoChange({
                          target: {
                            name: `${person.name}CountryCode`,
                            value: "",
                          },
                        });
                        handlePropertyInfoChange({
                          target: {
                            name: `${person.name}PhoneNumber`,
                            value: value || "",
                          },
                        });
                      }
                    }}
                    placeholder={`Enter ${person.label} Number`}
                    name={`${person.name}PhoneNumber`}
                    maxLength={15}
                    dropdownHeight="120px"
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Advance Amount
              </label>
              <input
                type="number"
                name="advanceAmount"
                value={propertyInfo?.advanceAmount || ""}
                onChange={handlePropertyInfoChange}
                onWheel={(e) => e.target.blur()}
                className="flex-1 px-4 py-2 input-focus no-arrow"
                placeholder="Enter Advance Amount"
                min="0"
                step="any"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Property Time Duration
              </label>
              <input
                type="text"
                name="propertyTimeDuration"
                value={propertyInfo?.propertyTimeDuration || ""}
                onChange={handlePropertyInfoChange}
                className="flex-1 px-4 py-2 input-focus"
                placeholder="Enter Property Time Duration"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                name="propertyTimeDurationStartDate"
                value={propertyInfo?.propertyTimeDurationStartDate || ""}
                disabled={
                  propertyInfo?.propertyTimeDurationStartDate ? isEdit : false
                }
                onChange={handlePropertyInfoChange}
                className="flex-1 px-4 py-2 input-focus"
                placeholder="Select Start Date"
                max={new Date().toISOString().split("T")[0]} // ⛔ prevent future dates
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                <label className="w-40 text-sm font-medium text-gray-700">
                  {propertyInfo?.propertyTimeDurationStartDate
                    ? "End Date"
                    : "Select Start Date First"}
                </label>
              </label>
              <input
                type="date"
                name="propertyTimeDurationEndDate"
                value={propertyInfo?.propertyTimeDurationEndDate || ""}
                onChange={handlePropertyInfoChange}
                disabled={
                  (propertyInfo?.propertyTimeDurationEndDate && isEdit) ||
                  !propertyInfo?.propertyTimeDurationStartDate
                }
                className="flex-1 px-4 py-2 input-focus"
                placeholder={
                  propertyInfo?.propertyTimeDurationStartDate
                    ? "Select End Date"
                    : "Select Start Date First"
                }
                min={propertyInfo?.propertyTimeDurationStartDate || ""} // ⛔ cannot be before Start Date
                max={new Date().toISOString().split("T")[0]} // ⛔ prevent future dates
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Advance Amount Paid
              </label>
              <CustomSelect
                options={[
                  { value: "Owner", label: "Owner" },
                  { value: "Mediator", label: "Mediator" },
                  { value: "Vendor", label: "Vendor" },
                ]}
                value={
                  propertyInfo?.advanceAmountPaidTo
                    ? {
                        value: propertyInfo.advanceAmountPaidTo,
                        label: propertyInfo.advanceAmountPaidTo,
                      }
                    : null
                }
                onChange={(selectedOption) =>
                  handlePropertyInfoChange({
                    target: {
                      name: "advanceAmountPaidTo",
                      value: selectedOption?.value || "",
                    },
                  })
                }
                placeholder="Select Option"
                isClearable
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Seen Original Doc. or Not
              </label>
              <CustomSelect
                options={[
                  { value: true, label: "Seen" },
                  { value: false, label: "Not Seen" },
                ]}
                value={
                  propertyInfo?.seenOriginalDocOrNot !== undefined
                    ? {
                        value: propertyInfo.seenOriginalDocOrNot,
                        label: propertyInfo.seenOriginalDocOrNot
                          ? "Seen"
                          : "Not Seen",
                      }
                    : null
                }
                onChange={(selectedOption) =>
                  handlePropertyInfoChange({
                    target: {
                      name: "seenOriginalDocOrNot",
                      value: selectedOption?.value ?? false,
                    },
                  })
                }
                placeholder="Select Option"
                isClearable
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Met Owner or Not
              </label>
              <CustomSelect
                options={[
                  { value: true, label: "Met" },
                  { value: false, label: "Not Met" },
                ]}
                value={
                  propertyInfo?.metOwnerOrNot !== undefined
                    ? {
                        value: propertyInfo.metOwnerOrNot,
                        label: propertyInfo.metOwnerOrNot ? "Met" : "Not Met",
                      }
                    : null
                }
                onChange={(selectedOption) =>
                  handlePropertyInfoChange({
                    target: {
                      name: "metOwnerOrNot",
                      value: selectedOption?.value ?? false,
                    },
                  })
                }
                placeholder="Select Option"
                isClearable
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm font-medium text-gray-700">
                Sitting Place
              </label>
              <input
                type="text"
                name="sittingPlace"
                value={propertyInfo?.sittingPlace || ""}
                onChange={handlePropertyInfoChange}
                className="flex-1 px-4 py-2 input-focus"
                placeholder="Enter Sitting Place"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Address Info",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div className="w-full  rounded-md overflow-hidden border">
            <MapWithPropertyAndData propertyInfo={propertyInfo} />
          </div>
          <div className="space-y-4">
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                <span className="border-red-400 border-l-4 mr-1 rounded-md"></span>
                Location
              </label>
              <div className="w-[50%]">
                <CustomSelect
                  options={locationData}
                  value={propertyInfo?.location || null}
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: { name: "location", value: selectedOption || "" },
                    })
                  }
                  placeholder="Select Location"
                  isSearchable
                />
              </div>
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Village
              </label>
              <div className="w-[50%]">
                <CustomSelect
                  options={locationData}
                  value={propertyInfo?.village || null}
                  onChange={(selectedOption) =>
                    handlePropertyInfoChange({
                      target: { name: "village", value: selectedOption || "" },
                    })
                  }
                  placeholder="Select village"
                  isSearchable
                />
              </div>
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                {getGeoLocationLabel()}
              </label>
              <input
                type="text"
                name="geoLocation"
                value={propertyInfo?.geoLocation || ""}
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/°/g, "")
                    .replace(/[NSEW]/gi, "")
                    .replace(/\s+/g, "");
                  handlePropertyInfoChange({
                    target: { name: "geoLocation", value: cleaned },
                  });
                }}
                placeholder="e.g., 12.9249, 80.1000"
                className="w-[50%] px-3 py-2 border rounded-md input-focus"
              />
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Address
              </label>
              <textarea
                name="address"
                value={propertyInfo?.address || ""}
                onChange={handlePropertyInfoChange}
                placeholder="Address"
                className="w-[50%] h-20 px-3 py-2 border rounded-md input-focus"
              />
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Landmark
              </label>
              <textarea
                name="landmark"
                rows={2}
                value={propertyInfo?.landmark || ""}
                onChange={handlePropertyInfoChange}
                placeholder="Near"
                className="w-[50%] px-3 py-2 border rounded-md input-focus"
              />
            </div>
            <div className="flex items-center">
              <label className="w-[50%] block text-sm font-medium text-gray-700">
                Location Link
              </label>
              <input
                type="text"
                name="locationLink"
                value={propertyInfo?.locationLink || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handlePropertyInfoChange({
                    target: { name: "locationLink", value },
                  });
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const gmapRegex =
                    /^(https?:\/\/)?(www\.)?(google\.[a-z.]+\/maps|goo\.gl\/maps)\/.+$/;
                  if (value && !gmapRegex.test(value)) {
                    showErrorToast("Please enter a valid Google Maps link");
                  }
                }}
                placeholder="Enter Google Maps link"
                className="w-[50%] px-3 py-2 border rounded-md input-focus"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Media Info",
      content: (
        <div className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6">
            <MediaUpload
              mediaType="Images"
              files={propertyInfo?.images || []}
              onChange={(urls) => handleArrayFieldChange("images", urls)}
              accept="image/*"
              maxFiles={5}
              folderpath="property_prospect_documents"
            />
          </div>
          <div className="space-y-4">
            {[
              { label: "Drive Link", name: "driveLink" },
              { label: "Sketch Link", name: "sketchLink" },
              { label: "Attached Link", name: "attachedLink" },
            ].map((link) => {
              const value = propertyInfo?.[link.name] || "";
              const urlRegex =
                /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
              const isInvalid = value && !urlRegex.test(value);

              return (
                <div key={link.name} className="flex flex-col gap-2">
                  <label className="w-40 text-sm font-medium text-gray-700 flex items-center">
                    {link.label}{" "}
                    <IoIosLink className="inline-block mb-1 ml-1 text-blue-600" />
                  </label>
                  <input
                    type="url"
                    name={link.name}
                    value={value}
                    onChange={(e) => {
                      handlePropertyInfoChange({
                        target: { name: link.name, value: e.target.value },
                      });
                    }}
                    className={`flex-1 px-4 py-2 input-focus ${
                      isInvalid ? "border-red-500" : ""
                    }`}
                    placeholder={`Enter ${link.label}`}
                  />
                  {isInvalid && (
                    <span className="text-red-500 text-xs">
                      Please enter a valid {link.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ),
    },
    {
      title: "Notes",
      content: (
        <div className="space-y-4 p-6">
          <div className="flex flex-col gap-2">
            <label className="w-40 text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              name="notes"
              rows={4}
              value={propertyInfo?.notes || ""}
              onChange={handlePropertyInfoChange}
              className="flex-1 px-4 py-2 input-focus"
              placeholder="Type your notes here..."
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <Accordion items={accordionItems} />
    </div>
  );
};

export default ProspectPropertyInfoBasic;
